import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useSession } from 'next-auth/react';
import { Box, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { logServerException } from 'appinsights/clientAppInsights';
import SubscriberModuleCard from 'components/common/SubscriberModuleCard';
import CardGrid from 'components/common/subcomponents/CardGrid';
import { hasModuleAccess } from '../../services/subscriptionUtils';
import PleaseWait from '../common/PleaseWait';
import { logClientException } from '../../appinsights/clientAppInsights';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { KitCard } from 'components/common/cms-kit';
import { extraComponentStyles } from './styles';

const defaultErrorMessage =
  'An unexpected error has occured. Please try again.';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MainFrame = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '@media screen and (min-width: 992px)': {
    width: '100%',
    padding: '25px 0 25px 0',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    padding: '5px 0 5px 0',
  },
});

const HeadingCardFrame = styled('div')({
  backgroundColor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0 2em',
  '@media screen and (min-width: 992px)': {
    margin: '40px 150px',
  },
  '@media screen and (max-width: 992px)': {
    margin: '10px 20px',
  },
});

const Container = styled('div')({
  marginTop: '30px',
  paddingBottom: '50px',
  '@media screen and (min-width: 992px)': {
    width: '100%',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
  },
});

const TitleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  '@media screen and (min-width: 992px)': {
    width: '100%',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
  },
});

const Title = styled('div')({
  margin: '25px 20px 0 0',
  fontWeight: 700,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  textTransform: 'uppercase',
  '@media screen and (min-width: 992px)': {
    fontSize: '1.75rem',
    lineHeight: '2.255rem',
  },
  '@media screen and (max-width: 992px)': {
    fontSize: '1.325rem',
    lineHeight: '1.575rem',
  },
});

const ProductName = styled('div')(({ theme }) => ({
  fontWeight: 600,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingBottom: '10px',
  color: theme.palette.common.black,
  '@media screen and (min-width: 992px)': {
    fontSize: '1.25rem',
    lineHeight: '1.313rem',
  },
  '@media screen and (max-width: 992px)': {
    fontSize: '1.425rem',
    lineHeight: '1.55rem',
  },
}));

const ShowSeriesLinkContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ShowSeriesLink = styled('div')({
  margin: '15px 0 -15px 0',
  fontWeight: 600,
  cursor: 'pointer',
  '@media screen and (min-width: 992px)': {
    fontSize: '1.1rem',
    lineHeight: '1.225rem',
  },
  '@media screen and (max-width: 992px)': {
    fontSize: '1rem',
    lineHeight: '1.125rem',
  },
});

const IntroductionBox = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.secondaryLightBlue50.main,
  margin: 0,
}));

const IntroContainer = styled('div')({
  '@media screen and (min-width: 992px)': {
    width: '100%',
    padding: '5px 15px 0 15px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    padding: '5px 5px 10px 5px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
});

const HeadingTextContainer = styled('div')({
  '@media screen and (min-width: 992px)': {
    width: '90%',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  '@media screen and (max-width: 992px)': {
    display: 'none',
  },
});

const HeadingTextMobileContainer = styled('div')({
  '@media screen and (min-width: 992px)': {
    display: 'none',
  },
  '@media screen and (max-width: 992px)': {
    width: '99%',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
});

const CompletionStat = styled('div')({
  backgroundColor: 'rgba(0, 136, 204, 1)',
  padding: '15px',
  display: 'flex',
  '@media screen and (min-width: 992px)': {
    width: '155px',
    margin: '0 20px 15px 20px',
    borderRadius: '6px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  '@media screen and (max-width: 992px)': {
    width: '75%',
    marginBottom: '15px',
    borderRadius: '0 6px 6px 0',
    height: '80px',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginLeft: '5px',
  },
});

const Complete100Banner = styled('div')({
  backgroundColor: 'transparent',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '@media screen and (min-width: 992px)': {
    margin: '0 20px 15px 20px',
  },
  '@media screen and (max-width: 992px)': {
    height: '80px',
    width: '80px',
    margin: '0 90px 0 90px',
  },
});

const StatText = styled('div')(({ theme }) => ({
  color: theme.palette.common.white,
  '@media screen and (min-width: 992px)': {
    fontSize: '1.125rem',
    lineHeight: '1.313rem',
    fontWeight: 600,
  },
  '@media screen and (max-width: 992px)': {
    fontSize: '1.125rem',
    lineHeight: '1.313rem',
    fontWeight: 600,
    margin: '0 15px 0 20px',
  },
}));

const StatComplete = styled('div')(({ theme }) => ({
  color: theme.palette.common.white,
  '@media screen and (min-width: 992px)': {
    fontSize: '3rem',
    lineHeight: '3.45rem',
    fontWeight: 700,
  },
  '@media screen and (max-width: 992px)': {
    fontSize: '3rem',
    lineHeight: '3.45rem',
    fontWeight: 700,
    marginRight: '20px',
  },
}));

const SeriesStatus = styled('div')(({ theme }) => ({
  color: theme.palette.common.white,
  display: 'flex',
  cursor: 'pointer',
  '@media screen and (min-width: 992px)': {
    marginTop: '10px',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  '@media screen and (max-width: 992px)': {
    margin: '15px 0 15px 0',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const BadgeBanner = styled('div')(({ theme }) => ({
  color: '#fff',
  background: `linear-gradient(45deg, ${theme.palette.info.light} 40%, #fafafa 50%, ${theme.palette.info.light} 60%)`,
  backgroundSize: '300%',
  backgroundPositionX: '100%',
  animation: 'shimmer 30s infinite linear',
  marginTop: '-10px',
  backgroundColor: theme.palette.info.light,
  padding: '0 25px',
  borderRadius: '5px',
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    top: '2px',
    right: '0.8px',
    width: 0,
    height: 0,
    borderLeft: '16px solid transparent',
    borderTop: '16px solid rgba(238, 245, 248, 1)',
    clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
    marginRight: '-1px',
  },
}));

const MobileBadgeBannerContainer = styled('div')({
  margin: '10px 0 10px 0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const SubscriberLandingHeading = (props) => {
  const { customData, module } = props;

  console.warn(
    '..........................SubscriberLandingHeading module.fields: ',
    module.fields,
  );

  const theme = useTheme();

  const [canAccessModules, setCanAccessModules] = useState(false);
  const [modules, setModules] = useState();
  const [modulesSet, setModulesSet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [showSeries, setShowSeries] = useState(true);
  const [subscriptionNew, setSubscriptionNew] = useState(false);
  const [subscriptionUpdated, setSubscriptionUpdated] = useState(false);

  const { data: session } = useSession();

  useEffect(() => {
    let moduleArray = [];
    customData.mods &&
      customData.mods.length > 0 &&
      customData.mods.forEach((mod, _i) => {
        moduleArray.push(mod);
      });
    setModules(moduleArray);
    setModulesSet(true);
    setShowSeries(module.fields.showSeriesonLoad === 'true' ? true : false);

    setTimeout(() => {
      badgeProduct(moduleArray);
    }, 2000);
  }, [module, customData]);

  useEffect(() => {
    setCanAccessModules(hasModuleAccess(session, 'wms'));
  }, [session]);

  useEffect(() => {
    if (modulesSet && canAccessModules) {
      fetchUserRegistrations();
    }
  }, [session?.user?.email, modulesSet, canAccessModules]);

  const getFetchOptions = (method, body) => {
    const options = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    if (body) {
      options.body = JSON.stringify(body);
    }
    return options;
  };

  const fetchUserRegistrations = async (_isOnClose = false) => {
    try {
      if (!session?.user?.email) {
        return;
      }
      setIsLoading(true);

      // get tags for all modules, since we need to check completion on all submodules
      const allSubMods = [];
      modules.forEach((mod) => {
        mod?.subMods?.forEach((sub) => {
          allSubMods.push(sub);
        });
      });
      const allTags = [];
      allSubMods.forEach((sub) => {
        sub.fields?.parentModuleTags?.split(',').forEach((tag) => {
          if (tag) {
            allTags.push(tag);
          }
        });
      });

      const body = {
        email: session?.user?.email,
        tags: allTags,
      };

      // fetch all registrations for the user
      let regs = [];
      const res = await fetch(
        '/api/scorm/fetch-registrations',
        getFetchOptions('POST', body),
      );
      if (res && res.ok && res.status === 200) {
        const bodyJson = await res.json();
        regs = bodyJson.registrations;

        let more = bodyJson.more;

        while (more && more.length > 0) {
          const newBody = {
            email: session?.user?.email,
            more: more,
          };
          const resmore = await fetch(
            '/api/scorm/fetch-registrations',
            getFetchOptions('POST', newBody),
          );
          if (resmore.ok && resmore.status === 200) {
            const morebodyJson = await resmore.json();
            const moreregs = morebodyJson.registrations;

            moreregs.forEach((morereg, _idx) => {
              regs.push(morereg);
            });

            const moreloop = morebodyJson.more;
            more = moreloop;
          }
        }
      }

      // now we have the user's registrations.
      // loop through registrations and mark sub completion status
      regs
        .sort((a, b) => {
          if (a.id > b.id) return 1;
          if (a.id < b.id) return -1;
          return 0;
        })
        .forEach((reg) => {
          const sub = allSubMods.find(
            (s) => s.fields?.scormCourse?.id === reg.course.id,
          );
          // Handle multiple records for single sub-module:
          // Only update if the instance is > than what is already there (this handles updates to courses)
          if (sub && (sub.instance < reg.instance || !sub.instance)) {
            sub.instance = reg.instance;
            sub.registrationCompletion = reg.registrationCompletion;
            sub.registrationSuccess = reg.registrationSuccess;
            sub.registeredVersion = reg.course.version;
          }
        });

      modules?.forEach((mod) => {
        let allComplete = true;
        let allNotStarted = true;

        mod?.subMods?.forEach((sub) => {
          if (sub.fields.hasNewVersion === 'true') {
            const versionNum = parseInt(sub.fields.newVersionNumber, 10);
            if (!isNaN(versionNum)) {
              if (sub.registeredVersion < versionNum) {
                sub.hasNewVersion = true;
                mod.hasNewVersion = true;
              }
            }
          }
          if (sub.fields.isNew === 'true') {
            sub.isNew = true;
            mod.isNew = true;
          }
          // Checking registrationCompletion values for all COMPLETED or all UNDEFINED (Not Started)
          switch (sub.registrationCompletion) {
            case 'COMPLETED':
              if (
                sub.registrationSuccess === 'UNKNOWN' ||
                sub.registrationSuccess === 'PASSED'
              ) {
                // passed
              } else {
                allComplete = false;
              }
              allNotStarted = false;
              break;
            case 'INCOMPLETE':
              allComplete = false;
              allNotStarted = false;
              break;
            default:
              allComplete = false;
              break;
          }
        });
        // Evaluate the vars to determine state
        if (allComplete) {
          mod.state = 'Completed';
        } else if (allNotStarted) {
          mod.state = 'Start';
        } else {
          mod.state = 'Continue';
        }
      });

      setIsLoading(false);
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      setIsLoading(false);
      console.log(error);
      logClientException(error);
    }
  };

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  const calculateCompletionPercent = (modules) => {
    if (!modules || modules.length === 0) return 0;
    let numMods = 0;
    let numSubMods = 0;
    let numSubModsCompleted = 0;
    modules.forEach((m) => {
      numMods = numMods + 1;
      m.subMods.forEach((s) => {
        if (s.fields.includeInCompletionStatus === 'true') {
          numSubMods = numSubMods + 1;
          if (s.registrationCompletion === 'COMPLETED') {
            numSubModsCompleted = numSubModsCompleted + 1;
          }
        }
      });
    });
    if (numSubMods === numSubModsCompleted) {
      return 100;
    } else {
      // console.log(
      //   'Completed: ',
      //   numSubModsCompleted,
      //   ' Total: ',
      //   numSubMods,
      //   ' Percent: ',
      //   (numSubModsCompleted / numSubMods) * 100 + '%',
      // );
      return (numSubModsCompleted / numSubMods) * 100;
    }
  };

  const badgeProduct = (mods) => {
    mods.forEach((mod) => {
      mod.subMods?.forEach((sub) => {
        if (sub.fields.isNew === 'true') {
          if (!subscriptionNew) setSubscriptionNew(true);
          sub.isNew = true;
          mod.isNew = true;
        } else if (sub.fields.hasNewVersion === 'true') {
          const versionNum = parseInt(sub.fields.newVersionNumber, 10);
          if (!isNaN(versionNum)) {
            if (sub.registeredVersion < versionNum) {
              if (!subscriptionUpdated) setSubscriptionUpdated(true);
              sub.hasNewVersion = true;
              mod.hasNewVersion = true;
            }
          }
        }
      });
    });
  };

  return (
    <MainFrame>
      <HeadingCardFrame>
        <TitleContainer>
          <Title style={{ color: module.fields.titleColor }}>
            {module.fields.title}
          </Title>
        </TitleContainer>
        <Box
          sx={{
            width: '100%',
            ml: 'auto',
            mr: 'auto',
            maxWidth: {
              sm: '540px',
              md: '768px',
              lg: '1200px',
              xl: '1200px',
            },
          }}
        >
          <KitCard
            style={{
              width: '100%',
              paddingBottom: '30px',
            }}
          >
            <IntroductionBox>
              <IntroContainer>
                <ImageContainer
                  style={{
                    backgroundImage: `url(${module?.fields?.headingImage?.url})`,
                    backgroundSize: 'contain',
                  }}
                ></ImageContainer>
                <HeadingTextContainer>
                  <ProductName>{module.fields.product}</ProductName>
                  <IntroText>{module.fields.description}</IntroText>
                  <SeriesStatus onClick={(_e) => setShowSeries(!showSeries)}>
                    {(subscriptionNew || subscriptionUpdated) && (
                      <>
                        <style>{extraComponentStyles(theme)}</style>
                        <BadgeBanner className="shimmer">NEW</BadgeBanner>
                      </>
                    )}
                    <span
                      style={{
                        color: '#000',
                        marginLeft: '15px',
                        marginTop: '-10px',
                      }}
                    >
                      {subscriptionNew
                        ? 'Module published'
                        : subscriptionUpdated
                          ? 'Module updated'
                          : ''}
                    </span>
                  </SeriesStatus>
                </HeadingTextContainer>
                {calculateCompletionPercent(modules) === 100 ? (
                  <Complete100Banner>
                    <img
                      src="https://content.liftwithboystown.org/images/100complete.svg"
                      style={{ width: 'auto', height: 'auto' }}
                      alt="Percent Complete: 100%"
                    />
                  </Complete100Banner>
                ) : (
                  <CompletionStat>
                    <StatText>Percent Completed</StatText>
                    <StatComplete>
                      {calculateCompletionPercent(modules).toFixed(0)}%
                    </StatComplete>
                  </CompletionStat>
                )}
              </IntroContainer>
              <HeadingTextMobileContainer>
                <ProductName>{module.fields.product}</ProductName>
                <IntroText>{module.fields.description}</IntroText>
                <MobileBadgeBannerContainer>
                  {(subscriptionNew || subscriptionUpdated) && (
                    <>
                      <style>{extraComponentStyles(theme)}</style>
                      <BadgeBanner className="shimmer">NEW</BadgeBanner>
                    </>
                  )}
                  <span
                    style={{
                      color: '#000',
                      marginLeft: '15px',
                      marginTop: '-10px',
                    }}
                  >
                    {subscriptionNew
                      ? 'Module published'
                      : subscriptionUpdated
                        ? 'Module updated'
                        : ''}
                  </span>
                </MobileBadgeBannerContainer>
              </HeadingTextMobileContainer>
            </IntroductionBox>
            <ShowSeriesLinkContainer>
              <ShowSeriesLink onClick={(_e) => setShowSeries(!showSeries)}>
                {showSeries ? (
                  <span>{module.fields.hideSeriesText}</span>
                ) : (
                  <span>{module.fields.showSeriesText}</span>
                )}{' '}
                {showSeries ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </ShowSeriesLink>
            </ShowSeriesLinkContainer>
            {/* {isMyLearning && session && canAccessModules && showSeries && ( */}
            {session && canAccessModules && showSeries && (
              <Container>
                <PleaseWait isLoading={isLoading} />
                <Snackbar
                  open={errorMessage}
                  autoHideDuration={6000}
                  onClose={handleAlertClose}
                >
                  <Alert
                    onClose={handleAlertClose}
                    severity="error"
                    sx={{ width: '100%' }}
                  >
                    {errorMessage}
                  </Alert>
                </Snackbar>
                <CardGrid style={{ margin: '-10px 10px -50px 10px' }}>
                  {modules.map((m) => (
                    <SubscriberModuleCard
                      key={m.contentID}
                      module={m}
                      buttonColor={module.fields.buttonColor}
                      buttonTextColor={module.fields.buttonTextColor}
                      cardHeaderColor={module.fields.cardHeaderColor}
                      cardHeaderTabColor={module.fields.cardHeaderTabColor}
                      cardHeaderTextColor={module.fields.cardHeaderTextColor}
                      hasAccess={canAccessModules}
                      canAccessModules={canAccessModules}
                    />
                  ))}
                </CardGrid>
              </Container>
            )}
          </KitCard>
        </Box>
      </HeadingCardFrame>
    </MainFrame>
  );
};
const resolveSeriesUrls = function (sitemap, posts) {
  let dynamicUrls = {};
  posts.forEach((post) => {
    Object.keys(sitemap).forEach((path) => {
      if (sitemap[path].contentID === post.contentID) {
        if (path.indexOf('well-managed-schools') > 0) {
          dynamicUrls[post.contentID] = path;
        }
      }
    });
  });
  return dynamicUrls;
};
// This is called during server-side rendering (not part of NextJS).
SubscriberLandingHeading.getCustomInitialProps = async ({
  agility,
  channelName,
  languageCode,
}) => {
  const api = agility;
  try {
    //get sitemap in order to get the dynamic urls
    let sitemap = await api.getSitemapFlat({
      channelName: channelName,
      locale: languageCode,
    });
    // get modules
    let raw = await api.getContentList({
      referenceName: 'ntrwmsmodulelist',
      locale: languageCode,
      contentLinkDepth: 2,
      // expandAllContentLinks: true,
      depth: 2,
      sort: 'fields.sortOrder',
      take: 50,
    });
    // get our sub modules
    // fetch all (limit of 250 per fetch)
    let subMods = await api.getContentList({
      referenceName: 'ntrwmssubmodulelist',
      locale: languageCode,
      sort: 'fields.subModuleName',
      depth: 3,
      take: 250,
    });
    const dynamicUrls = resolveSeriesUrls(sitemap, raw.items);
    let mods = raw.items.map((mod) => {
      const url = dynamicUrls[mod.contentID];
      if (url == undefined) {
        console.log(
          'Failed to get url for ' +
            mod.contentID +
            '  ' +
            mod.fields.moduleName,
        );
      }
      let subs = subMods.items
        .filter((s) => s.fields.parentModule.contentID === mod.contentID)
        .map((s) => {
          try {
            if (s.fields.scormCourse) {
              s.fields.scormCourse = JSON.parse(s.fields.scormCourse);
            }
          } catch (error) {
            console.log(error);
          }
          return s;
        });
      mod.url = url;
      mod.subMods = subs ? subs : null;
      return mod;
    });

    return {
      mods,
    };
  } catch (error) {
    await logServerException(error);
    if (console) console.error(error);
  }
};
const ImageContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    width: '165px',
    height: '160px',
    backgroundSize: '150%',
    backgroundRepeat: 'no-repeat',
    borderRadius: '6px',
  },
  [theme.breakpoints.down('md')]: {
    width: '80px',
    height: '80px',
    backgroundSize: '135%',
    backgroundRepeat: 'no-repeat',
    borderRadius: '6px 0 0 6px',
  },
}));

const IntroText = styled('div')(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: '1.063rem',
  lineHeight: '1.75rem',
  '& li': {
    '&::before': {
      backgroundColor: '#000 !important',
    },
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '10px',
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: '25px',
  },
}));

export default SubscriberLandingHeading;
